<template>
  <div data-testid="report-card-footer" class="z-10">
    <div v-if="reportHasAccess" class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <ButtonDownload
          ref="forecasts-button-download"
          data-testid="report-button-download"
          :data-filename="reportName"
          class="mx-0 !w-full !max-w-[100px] py-2.5 !text-sm md:!max-w-[150px]"
          :download-file="reportFile"
          :show-icon="false"
          variant="primary"
        />
        <div
          v-if="isDownloadLinkCopyable && showCopyLink"
          class="cursor-pointer"
          :title="reportMessageTooltip"
        >
          <Icon
            v-if="!reportCopied"
            name="IconClipboardLink"
            class="h-5 w-5"
            @click="copyRouteReport"
          />
          <Icon v-else name="IconCheck" class="h-6 w-6" />
        </div>
      </div>

      <Icon name="check-subscribe" class="h-8 w-8 stroke-p-900" />
    </div>
    <div v-else class="flex items-center justify-between">
      <ButtonContactUs
        text="Contact Us"
        variant="clean"
        :services="initialFields"
        class="bg-white !text-sm !text-black"
      />
      <Icon name="LockOutline" class="h-8 w-8 stroke-p-900" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { EventBus } from '~/utils/eventBus'
import ButtonDownload from '~/components/shared/common/ButtonDownload.vue'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import Icon from '~/components/common/Icon.vue'
import useAuthStore from '~/stores/auth'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { BUTTON_DOWNLOAD_ALL } from '~/src/constants/markets'
import type { ButtonDownloadType } from '~/src/types/common'
import { handlerButtonDownload } from '~/src/utils/buttons'
import { CAN_PREVIEW_CONTENT_ROLES } from '~/src/constants/auth'

type TProps = {
  reportHasAccess?: boolean
  reportName?: string
  reportFile?: object
  initialFields?: any[]
  reportId?: number
  showCopyLink?: boolean
  category?: string
  link?: string
}

const props = withDefaults(defineProps<TProps>(), {
  reportHasAccess: false,
  reportName: '',
  reportFile: () => ({}),
  initialFields: () => [],
  reportId: 0,
  showCopyLink: false,
  category: null,
  link: '',
})

const { $rudderstack } = useNuxtApp()
const authStore = useAuthStore()

const buttonDownload = useTemplateRef<ButtonDownloadType>(
  'forecasts-button-download',
)
const reportCopied = ref<boolean>(false)
const reportMessageTooltip = ref<string>('Copy url to clipboard')

const isUserAllowedToCopyDownloadLink = computed<boolean>(() =>
  authStore.userRoles.some((_role) => {
    return CAN_PREVIEW_CONTENT_ROLES.some(
      (_roleName) => _roleName === _role.toLowerCase(),
    )
  }),
)

const isDownloadLinkCopyable = computed<boolean>(
  () =>
    props.category &&
    !props.category.includes('control-documents') &&
    isUserAllowedToCopyDownloadLink.value,
)

const copyRouteReport = async () => {
  if (props.reportId && isDownloadLinkCopyable.value) {
    await copyToClipboard(props.link)
    reportCopied.value = true
    reportMessageTooltip.value = 'Url copied'

    $rudderstack.trackContentShare({
      share_method: 'link',
      content: {
        title: props.reportName,
        url: props.link,
      },
    })

    setTimeout(() => {
      reportCopied.value = false
      reportMessageTooltip.value = 'Copy url to clipboard'
    }, 1000)
  }
}

onMounted(() => {
  EventBus.$on('clickButtonCommon', (_payload) => {
    if (_payload === BUTTON_DOWNLOAD_ALL) handlerButtonDownload(buttonDownload)
  })
})

onBeforeUnmount(() => {
  EventBus.$off('clickButtonCommon')
})
</script>
